import { inject, type Ref } from "vue"
import { ModelController } from "@/controllers/model-controllers/abstract/model-controller"
import type { BaseModel } from "@/models/base.model"

type FormDataInjectorConfig = {
  controller?: ModelController<any, any, any>
  createHandler?: () => Promise<void>
  updateHandler?: () => Promise<void>
  customSaveHandler?: (save: () => Promise<void>) => Promise<void>
  autoClose?: boolean
}

export function useFormConfigurator<ModelType extends object>(config: FormDataInjectorConfig = {}) {
  const model: Ref<ModelType> = inject("model")!
  const initialText: string | undefined = inject("initialText")
  const isCreating: Ref<boolean> = inject("isCreating")!
  const autoClose: Ref<boolean> = inject("autoClose")!

  if (config.autoClose !== undefined) {
    autoClose.value = config.autoClose
  }

  const closeForm: () => void = inject("closeForm")!
  const saveHandle: (model: BaseModel) => void = inject("saveHandle")!

  // Variables that can be set for the wrapper
  const _controller: Ref<ModelController<any, any, any> | undefined> = inject("controller")!
  const _createHandler: Ref<(() => Promise<void>) | undefined> = inject("createHandler")!
  const _updateHandler: Ref<(() => Promise<void>) | undefined> = inject("updateHandler")!
  const _customSaveHandler: Ref<((save: () => Promise<void>) => Promise<void>) | undefined> =
    inject("customSaveHandler")!
  const _isFormInvalid: Ref<boolean> = inject("isFormInvalid")!

  _controller.value = config.controller
  _createHandler.value = config.createHandler
  _updateHandler.value = config.updateHandler
  _customSaveHandler.value = config.customSaveHandler
  _isFormInvalid.value = false

  return {
    model,
    initialText,
    isCreating,
    isFormInvalid: _isFormInvalid,
    closeForm,
    saveHandle
  }
}
